import React from 'react';
import type { ReactNode, Ref, ElementRef, HTMLProps } from 'react';

import { styled } from '@backyard-ui/styles';
import type { StitchesCSS } from '@backyard-ui/styles';

import { Icon } from '../..';
import { Spinner } from '../..';

import styles from './Button.styles';
export interface BaseProps {
  /**
   * The button appearance.
   */
  appearance?: 'critical' | 'info' | 'primary' | 'secondary' | 'warning';

  /**
   * The variant is used
   * to change the visual communication.
   */
  variant?: 'outline' | 'ghost' | 'solid';

  /**
   * Define the button size.
   */
  size?: 'xs' | 'sm' | 'md';

  /**
   * The HTML button `type` attribute
   */
  type?: 'submit' | 'button' | 'reset';

  /**
   * Display an icon.
   */
  iconLeft?: ReactNode;

  /**
   * Display an icon.
   */
  iconRight?: ReactNode;

  /**
   * Set disabled state.
   */
  isDisabled?: boolean;

  /**
   * Set loading state.
   */
  isLoading?: boolean;

  /**
   * Set button full width.
   */
  isFullWidth?: boolean;

  /**
   * Render `button` or `link`
   */
  as?: 'a' | 'button';

  /**
   * The ref to the HTML DOM element
   */
  ref?: Ref<HTMLButtonElement & HTMLAnchorElement>;

  /**
   *
   */
  css?: StitchesCSS;

  /**
   * The child nodes
   */
  children: ReactNode;
}

type LinkElProps = Omit<HTMLProps<HTMLAnchorElement>, 'size'>;
type ButtonElProps = Omit<HTMLProps<HTMLButtonElement>, 'size'>;

export type ButtonProps = BaseProps & ButtonElProps & LinkElProps;

const DEFAULT_TAG = 'button';

export const ButtonEl = styled(DEFAULT_TAG, styles);

const Button = React.forwardRef<ElementRef<typeof DEFAULT_TAG>, ButtonProps>(
  (props, ref) => {
    const {
      children,
      as,
      size = 'sm',
      iconLeft,
      iconRight,
      isDisabled = false,
      isLoading = false,
      ...rest
    } = props;

    return (
      <ButtonEl
        as={as}
        ref={ref}
        size={size}
        disabled={isDisabled || isLoading}
        {...rest}
      >
        {iconLeft && !isLoading && (
          <div
            className="Button-icon Button-icon--left"
            data-testid="Button-icon--left"
          >
            <Icon>{iconLeft}</Icon>
          </div>
        )}
        {isLoading ? <Spinner size={size} /> : children}
        {iconRight && !isLoading && (
          <div
            className="Button-icon Button-icon--right"
            data-testid="Button-icon--right"
          >
            <Icon>{iconRight}</Icon>
          </div>
        )}
      </ButtonEl>
    );
  }
);

Button.displayName = 'Button';

export default Button;
